import { useMemo, useRef, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { IFilterCompetence } from '@legacy/screens/ResourcePlanner/components/AssignFlow/components/FilterItems/FilterItemCompetence';
import { getApiDateFormat } from '@legacy/utils/date/date';
import { postFetch } from '@legacy/utils/fetcher';
import { POST_EMPLOYEE_SEARCH_KEY } from './postEmployeeSearchAPI.constants';
import {
  TEmployeeSearchReq,
  TEmployeeSearchReqCompetence,
  TEmployeeSearchReqDepartment,
  TEmployeeSearchReqLegalEntity,
  TEmployeeSearchReqManager,
  TEmployeeSearchRes,
} from './postEmployeeSearchAPI.types';
import { ASSIGN_TAKE_ITEMS } from '../../constants';
import { IFilter } from '@/legacy/screens/ResourcePlanner/components/AssignFlow/components/FilterItems/FilterItem/FilterItem.types';

const POST_EMPLOYEE_SEARCH_URL_PATH =
  '/api/v2/resource-planner/employee-search';

const postEmployeeSearch = (
  reqBody: TEmployeeSearchReq,
): Promise<TEmployeeSearchRes> =>
  postFetch({
    path: POST_EMPLOYEE_SEARCH_URL_PATH,
    key: POST_EMPLOYEE_SEARCH_KEY,
    body: JSON.stringify(reqBody),
  });

interface IUsePostEmployeeSearch {
  taskId: string;
  competence?: IFilterCompetence;
  department?: IFilter;
  endDate?: Date | null;
  hours?: number;
  legalEntity?: IFilter;
  manager?: IFilter;
  query?: string;
  startDate?: Date | null;
  take?: number;
  skip?: string;
}

export const usePostEmployeeSearch = ({
  competence,
  department,
  endDate,
  hours,
  legalEntity,
  manager,
  query,
  startDate,
  taskId,
  take = ASSIGN_TAKE_ITEMS,
  skip,
}: IUsePostEmployeeSearch) => {
  const Manager: TEmployeeSearchReqManager = manager?.values?.length
    ? {
        ManagerIds: manager.values.map((m) => m.value).toString(),
        IsManagerInclusive: manager.isInclude,
      }
    : {
        ManagerIds: undefined,
        IsManagerInclusive: undefined,
      };

  const Department: TEmployeeSearchReqDepartment = department?.values?.length
    ? {
        DepartmentIds: department.values.map((d) => d.value).toString(),
        IsDepartmentInclusive: department.isInclude,
      }
    : {
        DepartmentIds: undefined,
        IsDepartmentInclusive: undefined,
      };

  const LegalEntity: TEmployeeSearchReqLegalEntity = legalEntity?.values?.length
    ? {
        LegalEntityIds: legalEntity.values.map((l) => l.value).toString(),
        IsLegalEntityInclusive: legalEntity.isInclude,
      }
    : {
        LegalEntityIds: undefined,
        IsLegalEntityInclusive: undefined,
      };

  const Competence: TEmployeeSearchReqCompetence = competence?.values?.length
    ? {
        Competences: competence.values.map((c) => ({
          Id: c.value,
          Value: c.levels.toString(),
        })),
        IsCompetenceInclusive: true,
      }
    : {
        Competences: undefined,
        IsCompetenceInclusive: undefined,
      };

  const reqBody: TEmployeeSearchReq = {
    Hours: hours,
    PeriodEndDate: getApiDateFormat(endDate ?? new Date()),
    PeriodStartDate: getApiDateFormat(startDate ?? new Date()),
    SearchText: query?.trim(),
    TaskId: Number(taskId),
    Take: take,
    Skip: skip,
    ...Competence,
    ...Department,
    ...LegalEntity,
    ...Manager,
  };

  const filteredReqBody = Object.keys(reqBody).reduce((a, b) => {
    const key = b as keyof TEmployeeSearchReq;
    if (
      reqBody[key] !== undefined &&
      reqBody[key] !== null &&
      reqBody[key] !== ''
    ) {
      return { ...a, [key]: reqBody[key] };
    }
    return a;
  }, {} as TEmployeeSearchReq);

  const {
    data: tempDate,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    ...queryResult
  } = useInfiniteQuery<TEmployeeSearchRes>({
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.items?.length === 0 || (lastPage.items?.length ?? 0) < take
        ? undefined // Changed from null to undefined to avoid type issues
        : lastPage.nextSkip,
    queryKey: [POST_EMPLOYEE_SEARCH_KEY, filteredReqBody],
    queryFn: async ({ pageParam }) => {
      const pageParamNumber = Number(pageParam);
      let response: TEmployeeSearchRes;

      try {
        response = await postEmployeeSearch({
          ...filteredReqBody,
          Skip: String((filteredReqBody?.Take ?? 0) * pageParamNumber),
        });
      } catch (err) {
        console.error('postEmployeeSearch failed', err);
        throw err;
      }

      // Ensure the return value matches TEmployeeSearchRes type exactly
      return {
        ...response, // Include all original properties from the API response
        items: response.entities ?? [],
        nextSkip:
          (response.entities?.length ?? 0) < (filteredReqBody?.Take ?? 0)
            ? undefined // Use 0 instead of null since nextSkip expects a number
            : pageParamNumber + 1,
      } as TEmployeeSearchRes; // Add type assertion to ensure compatibility
    },
  });

  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (isFetchingNextPage) return;

      // Disconnect previous observer if it exists
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create new observer if we have a node and more pages to fetch
      if (node && hasNextPage) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              fetchNextPage();
            }
          },
          { threshold: 1.0 },
        );

        observerRef.current.observe(node);
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const data = useMemo(
    () => tempDate?.pages?.flatMap((page) => page.items) ?? [],
    [tempDate?.pages],
  );

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    lastElementRef, // Return this instead of observerRef
    latestPageChildrenLength: tempDate?.pages?.at(-1)?.items?.length ?? 0,
    ...queryResult,
  };
};
